import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { InlineLoader, EmptyState } from "@/components/shared";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Eye, Share2, Heart, DollarSign, TrendingUp, Repeat, BarChart3, Radio, Megaphone,
} from "lucide-react";

const RANGES = ["7", "30", "90"];
const fmtDate = (d) => {
  const [, mm, dd] = (d || "").split("-");
  return mm && dd ? `${Number(mm)}/${Number(dd)}` : d;
};
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

export default function OrganizerAnalytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30");
  const [campaignId, setCampaignId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { days: range };
    if (campaignId !== "all") params.campaign_id = campaignId;
    api.get("/organizer/analytics", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range, campaignId]);

  if (loading && !data) return <InlineLoader label="Loading your analytics" />;
  if (!data) return <EmptyState icon={BarChart3} title="Analytics unavailable" message="We couldn't load your analytics right now." testid="analytics-error" />;

  const s = data.summary;
  const hasCampaigns = (data.campaigns || []).length > 0;

  return (
    <div data-testid="organizer-analytics-page">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Analytics</h1>
          <p className="mt-1 text-sm text-ff-medium">Views, shares, conversion and referrers — your whole funnel in one place.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="analytics-campaign-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {data.campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`campaign-opt-${c.slug}`}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 rounded-lg bg-[#F3F4F6] p-1" data-testid="analytics-range-toggle">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                className={`rounded-md px-3 py-1 text-xs font-bold transition-colors duration-200 ${range === r ? "bg-white text-ff-blue shadow-sm" : "text-ff-medium hover:text-ff-blue"}`}>
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {!hasCampaigns ? (
        <EmptyState icon={Megaphone} title="No campaigns yet"
          message="Publish a fundraiser to start collecting views, shares and donations."
          action={<Link to="/start-a-fundraiser" className="rounded-lg bg-ff-action px-4 py-2 text-sm font-semibold text-white">Start a fundraiser</Link>}
          testid="analytics-no-campaigns" />
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="analytics-kpis">
            <Kpi icon={Eye} label="Page Views" value={s.total_views.toLocaleString()} accent="blue" testid="kpi-views" />
            <Kpi icon={Share2} label="Shares" value={s.total_shares.toLocaleString()} accent="red" testid="kpi-shares" />
            <Kpi icon={Heart} label="Donations" value={s.total_donations.toLocaleString()} accent="red" testid="kpi-donations" />
            <Kpi icon={DollarSign} label="Raised" value={money(s.total_raised)} accent="green" testid="kpi-raised" />
            <Kpi icon={TrendingUp} label="View → Donation" value={pct(s.view_to_donation_rate)} accent="blue" testid="kpi-view-conv" hint="Of everyone who viewed a page" />
            <Kpi icon={Radio} label="Share → Donation" value={pct(s.share_to_donation_rate)} accent="red" testid="kpi-share-conv" hint="Of every share that went out" />
            <Kpi icon={DollarSign} label="Avg Gift" value={money(s.avg_gift)} accent="green" testid="kpi-avg-gift" />
            <RecurringSplit recurring={s.recurring_count} oneTime={s.one_time_count} />
          </div>

          {/* Traffic & conversion chart */}
          <div className="mt-6 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="traffic-chart-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#1D4ED8]"><Eye className="h-5 w-5" /></span>
              <div>
                <div className="font-heading font-bold text-ff-blue">Traffic &amp; conversion</div>
                <div className="text-sm text-ff-medium">Page views vs donations over time — is your audience converting?</div>
              </div>
            </div>
            <div className="mt-5 h-64 w-full" data-testid="traffic-chart" role="img"
              aria-label={`Traffic and conversion over the last ${data.days} days: ${s.total_views} views and ${s.total_donations} donations.`}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.series} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1D4ED8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#1D4ED8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6B7280" }} minTickGap={24} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={30} />
                  <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip labelFormatter={fmtDate} contentStyle={{ borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                  <Area yAxisId="left" type="monotone" dataKey="views" name="Views" stroke="#1D4ED8" strokeWidth={2.5} fill="url(#viewsFill)" />
                  <Line yAxisId="right" type="monotone" dataKey="donations" name="Donations" stroke="#C62828" strokeWidth={2.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Money over time chart */}
          <div className="mt-6 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="money-chart-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ECFDF5] text-ff-green"><DollarSign className="h-5 w-5" /></span>
              <div>
                <div className="font-heading font-bold text-ff-blue">Money raised over time</div>
                <div className="text-sm text-ff-medium">Daily donations in USD across the selected range.</div>
              </div>
            </div>
            <div className="mt-5 h-56 w-full" data-testid="money-chart" role="img"
              aria-label={`Money raised over the last ${data.days} days: ${money(s.total_raised)} total.`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.series} margin={{ top: 8, right: 4, left: -6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6B7280" }} minTickGap={24} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                  <Tooltip labelFormatter={fmtDate} formatter={(v) => [money(v), "Raised"]} contentStyle={{ borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Bar dataKey="raised" name="Raised" fill="#16A34A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top referrers */}
          <div className="mt-6 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="referrers-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF2F2] text-ff-red"><Radio className="h-5 w-5" /></span>
              <div>
                <div className="font-heading font-bold text-ff-blue">Top referrers</div>
                <div className="text-sm text-ff-medium">Where your donors come from — double down on what works.</div>
              </div>
            </div>
            {data.top_referrers.length === 0 ? (
              <div className="mt-4 rounded-lg border border-dashed border-ff-border p-6 text-center text-sm text-ff-medium">No traffic recorded in this range yet.</div>
            ) : (
              <div className="mt-4 overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 border-b border-ff-border pb-2 text-[11px] font-bold uppercase tracking-wide text-ff-medium">
                  <span>Channel</span><span className="text-right">Views</span><span className="text-right">Donations</span><span className="text-right">Raised</span>
                </div>
                {data.top_referrers.map((r) => {
                  const maxViews = Math.max(1, ...data.top_referrers.map((x) => x.views));
                  return (
                    <div key={r.channel} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 py-2.5" data-testid={`referrer-${r.channel}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-ff-blue">{r.label}</div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                          <div className="h-full rounded-full bg-[#1D4ED8]" style={{ width: `${Math.round((r.views / maxViews) * 100)}%` }} />
                        </div>
                      </div>
                      <span className="text-right text-sm font-semibold text-ff-dark">{r.views.toLocaleString()}</span>
                      <span className="text-right text-sm font-semibold text-ff-dark">{r.donations}</span>
                      <span className="text-right text-sm font-bold text-ff-green">{money(r.raised)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "blue", testid, hint }) {
  const bg = accent === "red" ? "bg-ff-red" : accent === "green" ? "bg-ff-green" : "bg-ff-blue";
  return (
    <div className="rounded-xl border border-ff-border bg-white p-4 shadow-sm" data-testid={testid}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg}`}>
        <Icon className="h-4.5 w-4.5 text-white" />
      </span>
      <div className="mt-3 font-heading text-xl font-extrabold text-ff-blue" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ff-medium">{label}</div>
      {hint && <div className="mt-0.5 text-[10px] leading-tight text-ff-medium/80">{hint}</div>}
    </div>
  );
}

function RecurringSplit({ recurring, oneTime }) {
  const total = recurring + oneTime;
  const recPct = total ? Math.round((recurring / total) * 100) : 0;
  return (
    <div className="rounded-xl border border-ff-border bg-white p-4 shadow-sm" data-testid="kpi-recurring">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ff-blue">
        <Repeat className="h-4.5 w-4.5 text-white" />
      </span>
      <div className="mt-3 font-heading text-xl font-extrabold text-ff-blue">{recPct}%</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ff-medium">Recurring</div>
      <div className="mt-0.5 text-[10px] leading-tight text-ff-medium/80">{recurring} monthly · {oneTime} one-time</div>
    </div>
  );
}
